import {
  useCallback,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { objectKeysToCamelCase, type KeysToCamelCase } from '@mint-lib/api';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useProfile } from '@mint-lib/profile-helpers';
import { useQueryClient } from '@mint-lib/query-client';
import * as DataIngestion from '@mint-sdk/api-data-ingestion';
import { useRouter } from '@mint/routing';
import { Socket } from 'socket.io-client';

import { TABS } from '../../../constants/importHubTabs.js';
import { useDataFileContext } from '../../../contexts/DataFileContext/useDataFileContext.js';
import { useDataFileSocket } from '../../../hooks/index.js';
import {
  useDataFileRouteApi,
  useImportHubPaths,
} from '../../../hooks/useImportHubRouteApi.js';
import { useUsersInfo } from '../../../queries/index.js';

type TakeOverRequest = {
  table_data_file_id: string;
  user_id: number;
  action: 'request' | 'release';
};

type ServerTakeOverState = {
  is_locked: boolean;
  locked_by: number | null;
  requested_by: number | null;
  last_transfer_cause: null | 'user' | 'timeout';
  is_wizard_finished: boolean;
};

type TakeOverState = KeysToCamelCase<ServerTakeOverState>;

type InternalState = {
  current: TakeOverState;
  previous: TakeOverState;
};

const initialTakeOverState: TakeOverState = {
  isLocked: false,
  lockedBy: null,
  requestedBy: null,
  lastTransferCause: null,
  isWizardFinished: false,
};

const getTakeoverFlags = (
  current: TakeOverState,
  previous: TakeOverState,
  userId: number | undefined,
) => {
  const isInitialState = previous === initialTakeOverState;
  const hasDataFileBeenFinished = current.isWizardFinished;

  const isLockedByMe = current.isLocked && current.lockedBy === userId;
  const isPreviouslyLockedByMe = previous.lockedBy === userId;
  const isLockedByAnotherUser = current.isLocked && current.lockedBy !== userId;
  const isPreviouslyLockedByAnotherUser =
    previous.isLocked && previous.lockedBy !== userId;

  const isRequestedByAnotherUser =
    isLockedByMe &&
    Boolean(current.requestedBy) &&
    current.requestedBy !== userId;
  const userIdRequestedBy = current.requestedBy;

  const isPreviouslyRequestedByMe = previous.requestedBy === userId;
  const isRequestHandled =
    previous.requestedBy !== null && current.requestedBy === null;

  const hasBeenTransferedToMe = isPreviouslyLockedByAnotherUser && isLockedByMe;
  const hasBeenReleased = isPreviouslyLockedByMe && !isLockedByMe;
  const hasBeenReleasedByMe =
    hasBeenReleased && current.lastTransferCause === 'user';
  const hasBeenReleasedByTimeout =
    hasBeenReleased && current.lastTransferCause === 'timeout';
  const hasBeenRejectedByAnotherUser =
    isPreviouslyRequestedByMe && isRequestHandled && isLockedByAnotherUser;

  return {
    isInitialState,
    hasDataFileBeenFinished,
    isLockedByMe,
    isLockedByAnotherUser,
    isRequestHandled,
    isRequestedByAnotherUser,
    userIdRequestedBy,
    hasBeenTransferedToMe,
    hasBeenReleasedByMe,
    hasBeenReleasedByTimeout,
    hasBeenRejectedByAnotherUser,
  };
};

export const useDataFileTakeoverState = () => {
  const { t } = useTranslation('@myn/import-hub');
  const router = useRouter();
  const navigate = useDataFileRouteApi().useNavigate();
  const paths = useImportHubPaths();
  const queryClient = useQueryClient();

  const user = useProfile();
  const { dataFileId } = useDataFileContext();
  const userId = user.profile?.id;

  const stateRef = useRef<InternalState>({
    current: initialTakeOverState,
    previous: initialTakeOverState,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const releaseDataFile = useCallback(
    (socket: Socket) => {
      socket.emit('takeover', {
        table_data_file_id: dataFileId,
        user_id: userId,
        action: 'release',
      });
    },
    [dataFileId, userId],
  );

  const dataFileSocket = useDataFileSocket(dataFileId, {
    onCleanup: releaseDataFile,
  });

  const invalidateDataFileRoute = useCallback(async () => {
    const previewQueryKey =
      DataIngestion.DataFile.getTableDataFilePreviewV1QueryOptions({
        urlParams: { id: dataFileId },
      }).queryKey;
    queryClient.removeQueries({ queryKey: previewQueryKey });
    await router.invalidate({
      filter: (d) => {
        return d.routeId.includes('import-hub/performance/imports/$dataFileId');
      },
    });
    setIsSubmitting(false);
  }, [router, queryClient, dataFileId]);

  const navigateToImportsList = useCallback(async () => {
    await navigate({
      to: paths.performance,
      search: { tab: TABS.imports },
    });
  }, [navigate, paths.performance]);

  const handleEmitTakeoverRequest = useCallback(
    (action: TakeOverRequest['action']) => {
      if (dataFileSocket) {
        dataFileSocket.emit('takeover', {
          table_data_file_id: dataFileId,
          user_id: userId,
          action,
        });
      }
    },
    [dataFileId, dataFileSocket, userId],
  );

  const handleRequestDataFile = () => {
    setIsSubmitting(true);
    handleEmitTakeoverRequest('request');
  };

  const handleReleaseDataFile = useCallback(() => {
    setIsSubmitting(true);
    releaseDataFile(dataFileSocket);
  }, [releaseDataFile, dataFileSocket]);

  const handleRejectReleaseDataFile = () => {
    if (dataFileSocket) {
      const userIdRequestedBy = stateRef.current.current.requestedBy;
      dataFileSocket.emit('takeover', {
        table_data_file_id: dataFileId,
        user_id: userIdRequestedBy,
        action: 'release',
      });
    }
  };

  const subscribe = useCallback(
    (callback: () => void) => {
      if (!dataFileSocket) {
        return () => {
          /* empty */
        };
      }

      const handleStateChange = (newServerState: ServerTakeOverState) => {
        const newTakeOverState = objectKeysToCamelCase(newServerState);

        const prevState = stateRef.current.current;

        stateRef.current = {
          previous: prevState,
          current: newTakeOverState.isWizardFinished
            ? { ...prevState, isWizardFinished: true }
            : newTakeOverState,
        };

        const takeoverFlags = getTakeoverFlags(
          stateRef.current.current,
          stateRef.current.previous,
          userId,
        );

        if (
          takeoverFlags.isInitialState &&
          !stateRef.current.current.isLocked
        ) {
          handleEmitTakeoverRequest('request');
        }

        if (takeoverFlags.hasBeenTransferedToMe) {
          invalidateDataFileRoute();
          return;
        }

        if (takeoverFlags.hasBeenReleasedByMe) {
          navigateToImportsList();
          return;
        }

        if (takeoverFlags.hasBeenRejectedByAnotherUser) {
          navigateToImportsList();
          enqueueSnackbar({
            variant: 'warning',
            title: t('Access denied'),
            subTitle: t(
              'Your request to take control was declined by the current editor.',
            ),
            removable: true,
          });
          return;
        }

        if (takeoverFlags.isRequestHandled) {
          setIsSubmitting(false);
        }

        callback();
      };

      dataFileSocket.on('state_changed', handleStateChange);
      return () => {
        dataFileSocket.off('state_changed', handleStateChange);
      };
    },
    [
      t,
      dataFileSocket,
      userId,
      handleEmitTakeoverRequest,
      invalidateDataFileRoute,
      navigateToImportsList,
    ],
  );

  const getSnapshot = useCallback(() => {
    return stateRef.current;
  }, []);

  const { current, previous } = useSyncExternalStore(subscribe, getSnapshot);

  const takeoverFlags = useMemo(
    () => getTakeoverFlags(current, previous, userId),
    [current, previous, userId],
  );

  const {
    data: {
      list: [userRequestedBy],
    },
  } = useUsersInfo(
    {
      id: takeoverFlags.userIdRequestedBy,
      includeOwners: true,
    },
    { enabled: takeoverFlags.isRequestedByAnotherUser },
  );

  return {
    ...takeoverFlags,
    isSubmitting,
    userRequestedBy,
    handleRequestDataFile,
    handleReleaseDataFile,
    handleRejectReleaseDataFile,
    navigateToImportsList,
  };
};
